import { alpha } from "@mui/material/styles";
import { glass } from "../tokens/glass";
import { ink } from "../tokens/ink";
import { shapePill, heightRow } from "../../shape";

/**
 * Floating action pill — extracted verbatim from the gallery bottom bar
 * ("Share", "Shuffle it", "I'm Interested") so the same button can be
 * dropped anywhere an action floats over imagery.
 *
 * Two fills with identical geometry and typography, so they can sit side by
 * side — or swap places — without any layout shift:
 *   • ghost — transparent with shadowed white text, for secondary actions
 *     that must stay legible over whatever image sits behind them.
 *   • solid — white glass fill with near-black text, for the single
 *     most-prominent action in a row.
 *
 * Pair with the GlassActionButton component, which owns the markup (leading
 * icon wrapper, label) so call sites can't drift apart on typography.
 */

/**
 * Ghost fill only: the drop shadow goes on the svg, and the label leans on
 * the root textShadow — MUI v5's Button has no .MuiButton-label to target.
 */
export const glassActionIconFilter =
  "drop-shadow(0 0 0.75px rgba(0, 0, 0, 0.34)) drop-shadow(0 1px 1.5px rgba(0, 0, 0, 0.24))";
export const glassActionTextShadow =
  "0 0 0.75px rgba(0, 0, 0, 0.34), 0 1px 1.5px rgba(0, 0, 0, 0.24), 0 0 8px rgba(0, 0, 0, 0.1)";

/** Soft omnidirectional lift (ambient blur + light drop), no backdrop-filter. */
const chipGlassSx = {
  border: glass.border,
  background: glass.background,
  boxShadow: glass.shadowChipIdle,
};

const chipGlassActiveSx = {
  background: "rgba(255, 255, 255, 0.99)",
  borderColor: alpha("#000000", 0.12),
  boxShadow: glass.shadowChipActive,
};

const pillBorder = `1px solid ${alpha("#000000", 0.1)}`;
const pillShadow = `0 1px 3px ${alpha("#000000", 0.08)}`;

/** ~10% tighter horizontal padding than spacing(3). */
const pillPx = 2.7;

const pillShellSx = {
  borderRadius: shapePill,
  minHeight: heightRow,
  height: heightRow,
  px: pillPx,
  boxSizing: "border-box",
  minWidth: "min-content",
  width: "auto",
  textTransform: "none",
  whiteSpace: "nowrap",
  fontWeight: 600,
  fontSize: "15px",
  lineHeight: 1.2,
};

/** Lit state shared by hover / focus-visible on the ghost fill. */
const ghostLitSx = {
  ...chipGlassSx,
  ...chipGlassActiveSx,
  backgroundColor: "#FFFFFF",
  border: pillBorder,
  boxShadow: pillShadow,
  color: ink.primary,
  opacity: 1,
  textShadow: "none",
  "& .MuiButton-startIcon svg": {
    filter: "none !important",
    WebkitFilter: "none !important",
  },
};

const ghostSx = {
  ...pillShellSx,
  textShadow: glassActionTextShadow,
  color: "#FFFFFF",
  backgroundColor: "transparent",
  border: "1px solid transparent",
  boxShadow: "none",
  transition:
    "background-color 140ms ease, border-color 140ms ease, box-shadow 140ms ease, color 140ms ease, opacity 140ms ease",
  "& .MuiButton-startIcon": {
    color: "inherit",
    mr: "10px",
    ml: "-2px",
  },
  "& .MuiButton-startIcon svg": {
    filter: glassActionIconFilter,
  },
  "&:active": { opacity: 0.88 },
  "@media (hover: hover) and (pointer: fine)": {
    "&:hover": ghostLitSx,
    "&:focus-visible": ghostLitSx,
    "&:active": {
      ...ghostLitSx,
      backgroundColor: alpha("#F5F5F5", 0.98),
    },
  },
};

/** Lit state shared by hover / focus-visible on the solid fill. */
const solidLitSx = {
  ...chipGlassSx,
  ...chipGlassActiveSx,
  backgroundColor: "#FFFFFF",
  borderColor: alpha("#000000", 0.16),
  boxShadow: `0 2px 8px ${alpha("#000000", 0.12)}`,
  color: ink.primary,
  opacity: 1,
};

const solidSx = {
  ...pillShellSx,
  color: ink.primary,
  ...chipGlassSx,
  ...chipGlassActiveSx,
  border: pillBorder,
  boxShadow: pillShadow,
  transition:
    "background-color 140ms ease, border-color 140ms ease, box-shadow 140ms ease, opacity 140ms ease",
  "& .MuiButton-startIcon": {
    color: ink.primary,
    mr: "10px",
    ml: "-2px",
  },
  "& .MuiButton-startIcon svg": {
    filter: "none",
  },
  "&:active": { opacity: 0.9 },
  "@media (hover: hover) and (pointer: fine)": {
    "&:hover": solidLitSx,
    "&:focus-visible": solidLitSx,
    "&:active": {
      backgroundColor: alpha("#F5F5F5", 0.98),
      opacity: 1,
    },
  },
};

export function getGlassActionButtonSx({ variant = "ghost" } = {}) {
  return variant === "solid" ? solidSx : ghostSx;
}
